// Admin Panel - Cash Deposits Page
// File: admin-panel/src/pages/CashDeposits.jsx

import React, { useState, useEffect ,useContext} from 'react';
import { collection, query, onSnapshot, updateDoc, doc, orderBy, writeBatch } from 'firebase/firestore';
import { db } from '../../services/firebase'
import {
  BanknotesIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  EyeIcon,
  XMarkIcon,
  PhotoIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';

function DepositDetailsModal({ deposit, isOpen, onClose, onVerify, onReject }) {
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!isOpen || !deposit) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleVerify = async () => {
    setLoading(true);
    await onVerify(deposit.id);
    setLoading(false);
    onClose();
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    setLoading(true);
    await onReject(deposit.id, rejectionReason);
    setLoading(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gray-800 border border-gray-700 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-700 sticky top-0 bg-gray-800">
          <h2 className="text-xl font-bold text-white">Deposit Details</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-700 text-gray-400">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Amount */}
          <div className="text-center py-6 bg-gradient-to-r from-green-600/20 to-green-700/20 border border-green-500/30 rounded-2xl">
            <p className="text-gray-400 mb-2">Deposit Amount</p>
            <p className="text-4xl font-bold text-green-400">₹{deposit.amount?.toLocaleString('en-IN')}</p>
          </div>

          {/* Porter Info */}
          <div className="flex items-center gap-4 bg-gray-700/30 rounded-xl p-4">
            <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
              <UserIcon className="w-6 h-6 text-red-400" />
            </div>
            <div>
              <p className="text-white font-medium">{deposit.porterName}</p>
              <p className="text-gray-400 text-sm">Porter ID: {deposit.porterId}</p>
            </div>
          </div>

          {/* Details */}
          <div className="space-y-4">
            <div className="flex justify-between py-3 border-b border-gray-700">
              <span className="text-gray-400">Reference Number</span>
              <span className="text-white font-medium">{deposit.referenceNumber || '-'}</span>
            </div>
            <div className="flex justify-between py-3 border-b border-gray-700">
              <span className="text-gray-400">Deposit Method</span>
              <span className="text-white font-medium capitalize">{deposit.depositMethod || 'Cash'}</span>
            </div>
            <div className="flex justify-between py-3 border-b border-gray-700">
              <span className="text-gray-400">Bank/Account</span>
              <span className="text-white font-medium">{deposit.bankAccount || '-'}</span>
            </div>
            <div className="flex justify-between py-3 border-b border-gray-700">
              <span className="text-gray-400">Submitted At</span>
              <span className="text-white font-medium">{formatDate(deposit.createdAt)}</span>
            </div>
            <div className="flex justify-between py-3 border-b border-gray-700">
              <span className="text-gray-400">Status</span>
              <span className={`px-3 py-1 rounded-lg text-sm font-medium ${
                deposit.status === 'verified' ? 'bg-green-500/20 text-green-400' :
                deposit.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                'bg-yellow-500/20 text-yellow-400'
              }`}>
                {deposit.status?.toUpperCase()}
              </span>
            </div>
          </div>

          {/* Notes */}
          {deposit.notes && (
            <div>
              <p className="text-gray-400 text-sm mb-2">Notes</p>
              <p className="text-white bg-gray-700/30 rounded-xl p-4">{deposit.notes}</p>
            </div>
          )}

          {/* Proof Image */}
          {deposit.proofImageUrl && (
            <div>
              <p className="text-gray-400 text-sm mb-2">Deposit Proof</p>
              <img 
                src={deposit.proofImageUrl} 
                alt="Deposit proof" 
                className="w-full rounded-xl border border-gray-700"
              />
            </div>
          )}

          {/* Task References */}
          {deposit.taskIds?.length > 0 && (
            <div>
              <p className="text-gray-400 text-sm mb-2">Associated Tasks ({deposit.taskIds.length})</p>
              <div className="bg-gray-700/30 rounded-xl p-4 max-h-40 overflow-y-auto">
                {deposit.taskIds.map((taskId, idx) => (
                  <span key={taskId} className="inline-block mr-2 mb-2 px-3 py-1 bg-gray-600 text-gray-300 text-sm rounded-lg">
                    {taskId}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Rejection Reason (if rejected) */}
          {deposit.status === 'rejected' && deposit.rejectionReason && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
              <p className="text-red-400 text-sm font-medium mb-1">Rejection Reason</p>
              <p className="text-white">{deposit.rejectionReason}</p>
            </div>
          )}

          {/* Actions */}
          {deposit.status === 'submitted' && (
            <>
              {!showRejectForm ? (
                <div className="flex items-center gap-4 pt-4">
                  <button
                    onClick={() => setShowRejectForm(true)}
                    className="flex-1 py-3 bg-red-600/20 hover:bg-red-600/30 text-red-400 font-medium rounded-xl transition-colors"
                  >
                    Reject
                  </button>
                  <button
                    onClick={handleVerify}
                    disabled={loading}
                    className="flex-1 py-3 bg-green-600 hover:bg-green-500 text-white font-medium rounded-xl transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Processing...' : 'Verify & Approve'}
                  </button>
                </div>
              ) : (
                <div className="space-y-4 pt-4">
                  <div>
                    <label className="block text-gray-400 text-sm font-medium mb-2">Rejection Reason *</label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Explain why this deposit is being rejected..."
                      rows={3}
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => { setShowRejectForm(false); setRejectionReason(''); }}
                      className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-xl"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleReject}
                      disabled={loading}
                      className="flex-1 py-3 bg-red-600 hover:bg-red-500 text-white font-medium rounded-xl disabled:opacity-50"
                    >
                      {loading ? 'Processing...' : 'Confirm Rejection'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function DepositCard({ deposit, onView }) {
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusStyle = (status) => {
    const styles = {
      submitted: 'bg-yellow-500/20 text-yellow-400 border-yellow-500',
      verified: 'bg-green-500/20 text-green-400 border-green-500',
      rejected: 'bg-red-500/20 text-red-400 border-red-500',
    };
    return styles[status] || styles.submitted;
  };

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6 hover:border-gray-600 transition-all">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
            <BanknotesIcon className="w-6 h-6 text-green-400" />
          </div>
          <div>
            <p className="text-2xl font-bold text-white">₹{deposit.amount?.toLocaleString('en-IN')}</p>
            <p className="text-gray-400 text-sm">{deposit.porterName}</p>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-lg text-xs font-medium border ${getStatusStyle(deposit.status)}`}>
          {deposit.status?.toUpperCase()}
        </span>
      </div>

      <div className="space-y-2 mb-4">
        {deposit.referenceNumber && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Reference</span>
            <span className="text-white">{deposit.referenceNumber}</span>
          </div>
        )}
        <div className="flex justify-between text-sm">
          <span className="text-gray-400">Method</span>
          <span className="text-white capitalize">{deposit.depositMethod || 'Cash'}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-400">Submitted</span>
          <span className="text-white">{formatDate(deposit.createdAt)}</span>
        </div>
        {deposit.taskIds?.length > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Tasks</span>
            <span className="text-white">{deposit.taskIds.length} deliveries</span>
          </div>
        )}
      </div>

      {deposit.proofImageUrl && (
        <div className="mb-4 flex items-center gap-2 text-blue-400 text-sm">
          <PhotoIcon className="w-4 h-4" />
          <span>Proof attached</span>
        </div>
      )}

      <button
        onClick={() => onView(deposit)}
        className="w-full flex items-center justify-center gap-2 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium rounded-lg transition-colors"
      >
        <EyeIcon className="w-4 h-4" />
        View Details
      </button>
    </div>
  );
}

function SparesCashDeposits() {
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('submitted');
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    const depositsQuery = query(
      collection(db, 'cashDeposits'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(depositsQuery, (snapshot) => {
      const depositsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDeposits(depositsList);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleVerify = async (depositId) => {
    try {
      const deposit = deposits.find(d => d.id === depositId);
      
      // Update deposit status
      await updateDoc(doc(db, 'cashDeposits', depositId), {
        status: 'verified',
     verifiedBy: currentUser?.email || 'admin',
        verifiedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      // Update all associated tasks - mark cash as deposited
      if (deposit?.taskIds && deposit.taskIds.length > 0) {
        const batch = writeBatch(db);
        
        for (const taskId of deposit.taskIds) {
          const taskRef = doc(db, 'tasks', taskId);
          batch.update(taskRef, {
            'payment.deposited': true,
            'payment.depositPending': false,
            'payment.depositedAt': new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          });
        }
        
        await batch.commit();
      }

      alert('Deposit verified successfully!');
    } catch (error) {
      console.error('Verify error:', error);
      alert('Failed to verify deposit: ' + error.message);
    }
  };

  const handleReject = async (depositId, reason) => {
    try {
      const deposit = deposits.find(d => d.id === depositId);
      
      // Update deposit status
      await updateDoc(doc(db, 'cashDeposits', depositId), {
        status: 'rejected',
        rejectionReason: reason,
    rejectedBy: currentUser?.email || 'admin',
        rejectedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      // Reset tasks - remove deposit pending flag
      if (deposit?.taskIds && deposit.taskIds.length > 0) {
        const batch = writeBatch(db);
        
        for (const taskId of deposit.taskIds) {
          const taskRef = doc(db, 'tasks', taskId);
          batch.update(taskRef, {
            'payment.depositPending': false,
            'payment.depositId': null,
            updatedAt: new Date().toISOString(),
          });
        }
        
        await batch.commit();
      }

      alert('Deposit rejected');
    } catch (error) {
      console.error('Reject error:', error);
      alert('Failed to reject deposit: ' + error.message);
    }
  };

  const filteredDeposits = deposits.filter(d => {
    if (filter === 'all') return true;
    return d.status === filter;
  });

  const pendingCount = deposits.filter(d => d.status === 'submitted').length;
  const verifiedCount = deposits.filter(d => d.status === 'verified').length;
  const rejectedCount = deposits.filter(d => d.status === 'rejected').length;
  const totalPending = deposits
    .filter(d => d.status === 'submitted')
    .reduce((sum, d) => sum + (d.amount || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Cash Deposits</h1>
        <p className="text-gray-400 mt-1">Verify and manage porter cash deposits</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
          <p className="text-yellow-400 text-sm font-medium">Pending Verification</p>
          <p className="text-3xl font-bold text-white mt-1">{pendingCount}</p>
          <p className="text-yellow-400 text-sm mt-1">₹{totalPending.toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
          <p className="text-green-400 text-sm font-medium">Verified</p>
          <p className="text-3xl font-bold text-white mt-1">{verifiedCount}</p>
        </div>
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
          <p className="text-red-400 text-sm font-medium">Rejected</p>
          <p className="text-3xl font-bold text-white mt-1">{rejectedCount}</p>
        </div>
        <div className="bg-gray-700/50 border border-gray-600 rounded-xl p-4">
          <p className="text-gray-400 text-sm font-medium">Total Deposits</p>
          <p className="text-3xl font-bold text-white mt-1">{deposits.length}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {[
          { value: 'submitted', label: 'Pending', count: pendingCount },
          { value: 'verified', label: 'Verified', count: verifiedCount },
          { value: 'rejected', label: 'Rejected', count: rejectedCount },
          { value: 'all', label: 'All', count: deposits.length },
        ].map(opt => (
          <button
            key={opt.value}
            onClick={() => setFilter(opt.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === opt.value ? 'bg-red-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {opt.label} ({opt.count})
          </button>
        ))}
      </div>

      {/* Deposits Grid */}
      {filteredDeposits.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDeposits.map(deposit => (
            <DepositCard
              key={deposit.id}
              deposit={deposit}
              onView={setSelectedDeposit}
            />
          ))}
        </div>
      ) : (
        <div className="bg-gray-800 border border-gray-700 rounded-2xl p-12 text-center">
          <BanknotesIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">No Deposits Found</h3>
          <p className="text-gray-400">
            {filter !== 'all' ? 'No deposits match the selected filter' : 'No deposits have been submitted yet'}
          </p>
        </div>
      )}

      {/* Details Modal */}
      <DepositDetailsModal
        deposit={selectedDeposit}
        isOpen={!!selectedDeposit}
        onClose={() => setSelectedDeposit(null)}
        onVerify={handleVerify}
        onReject={handleReject}
      />
    </div>
  );
}

export default SparesCashDeposits;